/**
 * Mappe le FunnelFormData (UI) vers le payload attendu par l'Edge Function
 * Supabase `submit-lead` et effectue la soumission.
 *
 * Contrat côté serveur : komilfo-back-office/supabase/functions/submit-lead
 * + komilfo-back-office/docs/formulaire-webflow-lp.md
 */

import type { FunnelFormData } from "@/components/Funnel/Funnel";
import { readClickIds } from "@/lib/tracking";
import {
  CONSENT_TRAITEMENT_TEXT,
  CONSENT_MARKETING_TEXT,
  CONSENT_VERSION,
  MARKETING_CHANNELS,
} from "./consent";
import type { LocalisationMode } from "@/lib/localisationMode";

/** @deprecated conservé pour compat ; utiliser CONSENT_TRAITEMENT_TEXT. */
export const OPTIN_TEXT = CONSENT_TRAITEMENT_TEXT;

export type SubmitLeadErrorCode =
  | "captcha_missing"
  | "captcha_failed"
  | "optin_required"
  | "missing_fields"
  | "invalid_email"
  | "invalid_code_postal"
  | "invalid_code_insee"
  | "unknown_code_postal"
  | "unknown_code_insee"
  | "invalid_magasin"
  | "unknown_magasin"
  | "server_misconfigured"
  | "lead_insert_failed"
  | "invalid_json"
  | "network_error"
  | "unknown";

export interface SubmitLeadResult {
  ok: boolean;
  leadId?: string;
  magasinId?: string | null;
  magasinName?: string | null;
  error?: SubmitLeadErrorCode;
  fields?: string[];
  /** Message FR prêt à afficher à l'utilisateur. */
  message: string;
}

type SubmitLeadPayload = {
  turnstile_token: string;
  type: "devis" | "sav";
  nom: string;
  prenom: string;
  email: string;
  tel: string;
  code_postal: string;
  code_insee?: string;
  ville?: string;
  magasin_id?: string;
  message: string | null;
  optin: boolean;
  optin_text: string;
  marketing_optin: boolean;
  marketing_optin_text?: string;
  marketing_channels?: string[];
  consent_version: string;
  produits: string[];
  echeance?: string;
  travaux?: string;
  habitat?: string;
  contact_creneaux: string[];
  src: string;
  campaign: string;
  location_host: string;
  location_href: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
};

function resolveMessage(data: FunnelFormData, type: "devis" | "sav"): string | null {
  const raw = (type === "sav" ? data.savMessage : data.message) ?? "";
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function readLocation(): { host: string; href: string } {
  if (typeof window === "undefined") return { host: "", href: "" };
  return { host: window.location.host, href: window.location.href };
}

function readUtm(): { src: string; campaign: string } {
  if (typeof window === "undefined") return { src: "direct", campaign: "" };
  const params = new URLSearchParams(window.location.search);
  return {
    src: params.get("utm_source") ?? "direct",
    campaign: params.get("utm_campaign") ?? "",
  };
}

export function buildPayload(
  data: FunnelFormData,
  turnstileToken: string,
  mode: LocalisationMode,
): SubmitLeadPayload {
  const type: "devis" | "sav" = data.demande === "SAV" ? "sav" : "devis";
  const utm = readUtm();
  const loc = readLocation();
  const clickIds = readClickIds();

  const base: SubmitLeadPayload = {
    turnstile_token: turnstileToken,
    type,
    nom: data.nom.trim(),
    prenom: data.prenom.trim(),
    email: data.email.trim().toLowerCase(),
    tel: data.telephone.trim(),
    code_postal: data.codePostal.trim(),
    message: resolveMessage(data, type),
    optin: data.optin === true,
    optin_text: CONSENT_TRAITEMENT_TEXT,
    marketing_optin: data.marketingOptin === true,
    marketing_optin_text: data.marketingOptin === true ? CONSENT_MARKETING_TEXT : undefined,
    marketing_channels: data.marketingOptin === true ? [...MARKETING_CHANNELS] : undefined,
    consent_version: CONSENT_VERSION,
    produits: type === "devis" ? data.produits : [],
    echeance: type === "devis" && data.echeance ? data.echeance : undefined,
    travaux: type === "devis" && data.travaux ? data.travaux : undefined,
    habitat: type === "devis" && data.habitat ? data.habitat : undefined,
    contact_creneaux: data.contactPreference ?? [],
    src: utm.src,
    campaign: utm.campaign,
    location_host: loc.host,
    location_href: loc.href,
    gclid: clickIds.gclid,
    gbraid: clickIds.gbraid,
    wbraid: clickIds.wbraid,
  };

  if (mode === "magasin") {
    base.magasin_id = data.magasinId.trim();
    // Commune déclarée par le client. Sans elle, le serveur retombe sur la
    // première commune du code postal par ordre alphabétique — souvent fausse.
    // `code_insee` reste absent : il réveillerait resolve_lead_magasin, qui
    // écraserait le magasin choisi.
    const villeDeclaree = data.ville.trim();
    if (villeDeclaree) base.ville = villeDeclaree;
  } else {
    base.code_insee = data.codeInsee.trim().toUpperCase();
    base.ville = data.ville.trim();
  }

  return base;
}

function friendlyMessage(code: SubmitLeadErrorCode, fields?: string[]): string {
  switch (code) {
    case "captcha_missing":
    case "captcha_failed":
      return "Contrôle anti-robot échoué. Rechargez la page et réessayez.";
    case "optin_required":
      return "Vous devez accepter le traitement de vos données pour envoyer la demande.";
    case "missing_fields":
      return `Champs manquants : ${(fields ?? []).join(", ")}`;
    case "invalid_email":
      return "Adresse email invalide.";
    case "invalid_code_postal":
      return "Code postal invalide (5 chiffres attendus).";
    case "invalid_code_insee":
      return "Code commune invalide. Merci de resélectionner votre commune dans la liste.";
    case "unknown_code_postal":
      return "Nous n'intervenons pas encore sur ce code postal.";
    case "unknown_code_insee":
      return "Nous n'intervenons pas encore dans cette commune.";
    case "invalid_magasin":
    case "unknown_magasin":
      return "Magasin invalide. Merci de resélectionner votre magasin.";
    case "network_error":
      return "Connexion impossible. Vérifiez votre réseau.";
    case "server_misconfigured":
    case "lead_insert_failed":
    case "invalid_json":
    case "unknown":
    default:
      return "Une erreur est survenue. Merci de réessayer plus tard.";
  }
}

export async function submitLead(
  data: FunnelFormData,
  turnstileToken: string,
  mode: LocalisationMode,
): Promise<SubmitLeadResult> {
  const endpoint = import.meta.env.VITE_SUBMIT_LEAD_URL;
  if (!endpoint) {
    return {
      ok: false,
      error: "server_misconfigured",
      message: friendlyMessage("server_misconfigured"),
    };
  }

  const payload = buildPayload(data, turnstileToken, mode);
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  // Headers Supabase : `apikey` + `Authorization` sont requis par défaut sur
  // les Edge Functions (verify_jwt = true). L'anon key est publique par
  // design — la fonction reste protégée du spam par Turnstile côté serveur.
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (anonKey) {
    headers["apikey"] = anonKey;
    headers["Authorization"] = `Bearer ${anonKey}`;
  }

  try {
    const res = await fetch(endpoint, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      lead_id?: string;
      magasin_id?: string | null;
      magasin_name?: string | null;
      error?: SubmitLeadErrorCode;
      fields?: string[];
    };

    if (res.status === 201 && body.ok) {
      return {
        ok: true,
        leadId: body.lead_id,
        magasinId: body.magasin_id ?? null,
        magasinName: body.magasin_name ?? null,
        message: "Merci, nous revenons vers vous très vite.",
      };
    }

    const code: SubmitLeadErrorCode = body.error ?? "unknown";
    return {
      ok: false,
      error: code,
      fields: body.fields,
      message: friendlyMessage(code, body.fields),
    };
  } catch {
    return {
      ok: false,
      error: "network_error",
      message: friendlyMessage("network_error"),
    };
  }
}
